import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  ChevronRight,
  Archive,
  RotateCcw,
  MessageSquare,
  Phone,
} from "lucide-react";
import {
  useArchivedAgents,
  ArchivedAgent,
} from "@/hooks/agent/useArchivedAgents";
import { useUnarchiveAgent } from "@/hooks/agent/useUnarchiveAgent";
import { getConsistentColor } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface ArchivedAgentsSectionProps {
  onUnarchiveSuccess?: () => void;
}

const ArchivedAgentsSection = ({
  onUnarchiveSuccess,
}: ArchivedAgentsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { archivedAgents, isLoading, refetch } = useArchivedAgents();
  const { organization } = useAuth();
  const hideChatAgents = organization?.hide_chat_agents ?? true;
  const {
    unarchive,
    isUnarchiving,
    planLimitModalOpen,
    setPlanLimitModalOpen,
  } = useUnarchiveAgent();
  const navigate = useNavigate();

  const handleUnarchive = async (
    agentId: string,
    agentType: "chat" | "voice",
  ) => {
    const result = await unarchive(agentId, agentType);
    if (result === true) {
      refetch();
      onUnarchiveSuccess?.();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getAgentGradient = (id: string): string => {
    const colors = [
      "bg-gradient-to-br from-blue-400 to-blue-600",
      "bg-gradient-to-br from-purple-400 to-purple-600",
      "bg-gradient-to-br from-green-400 to-green-600",
      "bg-gradient-to-br from-orange-400 to-orange-600",
      "bg-gradient-to-br from-pink-400 to-pink-600",
      "bg-gradient-to-br from-indigo-400 to-indigo-600",
      "bg-gradient-to-br from-red-400 to-red-600",
      "bg-gradient-to-br from-yellow-400 to-yellow-600",
      "bg-gradient-to-br from-teal-400 to-teal-600",
      "bg-gradient-to-br from-cyan-400 to-cyan-600",
    ];

    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const filteredAgents =
    hideChatAgents === true
      ? archivedAgents.filter((a) => a.type === "voice")
      : archivedAgents;

  if (isLoading || filteredAgents.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 mt-8 pt-6">
      <div
        className="flex items-center justify-between py-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              isOpen ? "rotate-90" : ""
            }`}
          />
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-muted-foreground" />
            <div className="text-left">
              <h3 className="text-base font-medium text-gray-900">
                Archived agents
              </h3>
              <p className="text-sm text-muted-foreground font-normal">
                {filteredAgents.length} archived agent
                {filteredAgents.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4 space-y-4">
          {filteredAgents.map((agent) => {
            const gradientClass = getAgentGradient(agent.id);

            return (
              <Card
                key={agent.id}
                className="opacity-75 border border-gray-200 overflow-hidden bg-gray-50"
              >
                {/* Colored banner */}
                <div
                  className={`h-16 w-full ${gradientClass.replace("bg-gradient-to-br", "bg-gradient-to-r")} opacity-60`}
                ></div>

                <CardContent className="p-4 relative">
                  {/* Avatar overlapping banner */}
                  <div className="absolute -top-8 left-4">
                    <div
                      className={`relative p-0.5 rounded-full ${gradientClass}`}
                    >
                      <Avatar className="h-20 w-20 border-2 border-white">
                        <AvatarImage src={agent.avatar_url || ""} />
                        <AvatarFallback
                          className="text-sm font-bold text-white"
                          style={{
                            backgroundColor: `${getConsistentColor(agent.id)}80`,
                          }}
                        >
                          {getInitials(agent.agent_name || agent.title)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex items-center gap-4">
                    {/* Space for avatar */}
                    <div className="w-20 flex-shrink-0"></div>

                    {/* Agent Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-700 truncate">
                          {agent.agent_name || "Set a name"}
                        </h3>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            agent.type === "voice"
                              ? "bg-purple-50 text-purple-700 border-purple-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}
                        >
                          {agent.type === "voice" ? (
                            <>
                              <Phone className="h-3 w-3 mr-1" />
                              Voice
                            </>
                          ) : (
                            <>
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Chat
                            </>
                          )}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {agent.title || "Set a title"}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Archived</p>
                    </div>

                    {/* Unarchive Button */}
                    <div className="flex-shrink-0">
                      <Button
                        onClick={() =>
                          handleUnarchive(agent.id, agent.type || "chat")
                        }
                        disabled={isUnarchiving}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        {isUnarchiving ? (
                          <>
                            <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                            Unarchiving...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Unarchive
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Plan Limit Modal */}
      <Dialog open={planLimitModalOpen} onOpenChange={setPlanLimitModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>You have reached the limit!</DialogTitle>
            <DialogDescription>
              You have reached the max number of agents permitted in your plan.
              To create a new agent, you can either
              <ul className="list-disc pl-5 mt-2 mb-2">
                <li>
                  Archive existing agents (open the agent then click on the
                  contextual menu in top right corner)
                </li>
                <li>Upgrade your plan</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setPlanLimitModalOpen(false);
                navigate("/settings?tab=subscription");
              }}
              className="w-full"
            >
              Upgrade my plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivedAgentsSection;
